import { Directive, computed, input } from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { cva, type VariantProps } from 'class-variance-authority';

import * as fromAtomsColors from '../atoms/colors';
import * as fromHelpers from '../helpers';

export enum ButtonVariant {
  filled = 'filled',
  tonal = 'tonal',
  outline = 'outline',
  text = 'text',
}

export enum ButtonSizeVariant {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export enum ButtonColorVariant {
  primary = fromAtomsColors.ColorVariant.primary,
  neutral = fromAtomsColors.ColorVariant.neutral,
  error = fromAtomsColors.ColorVariant.error,
}

/**
 * Currently supported variants matrix
 *
 * | Type   | Color   | Size                      | Disabled |
 * |--------|---------|---------------------------|----------|
 * | Filled  | Primary | xs / sm / md / lg / xl   | True     |
 * | Filled  | Primary | xs / sm / md / lg / xl   | False    |
 * | Filled  | Neutral | xs / sm / md / lg / xl   | True     |
 * | Filled  | Neutral | xs / sm / md / lg / xl   | False    |
 * | Tonal   | Primary | xs / sm / md / lg / xl   | True     |
 * | Tonal   | Primary | xs / sm / md / lg / xl   | False    |
 * | Tonal   | Neutral | xs / sm / md / lg / xl   | True     |
 * | Tonal   | Neutral | xs / sm / md / lg / xl   | False    |
 * | Outline | Neutral | xs / sm / md / lg / xl   | True     |
 * | Outline | Neutral | xs / sm / md / lg / xl   | False    |
 * | Outline | Primary | xs / sm / md / lg / xl   | True     |
 * | Outline | Primary | xs / sm / md / lg / xl   | False    |
 * | Text    | Primary | xs / sm / md / lg / xl   | True     |
 * | Text    | Primary | xs / sm / md / lg / xl   | False    |
 * | Text    | Neutral | xs / sm / md / lg / xl   | True     |
 * | Text    | Neutral | xs / sm / md / lg / xl   | False    |
 */
export const buttonVariants = cva(
  fromHelpers.cn(
    'inline-flex items-center justify-center gap-2 pt-0.5',
    'typo-p2 font-medium rounded transition-colors',
    'hover:cursor-pointer',
    'disabled:opacity-50 disabled:pointer-events-none',
    'active:scale-[0.995]',
    '[&>svg]:mb-0.5',
  ),
  {
    variants: {
      variant: {
        [`${ButtonVariant.filled}`]: '', //default
        [`${ButtonVariant.tonal}`]: '',
        [`${ButtonVariant.outline}`]: '',
        [`${ButtonVariant.text}`]: '',
      },
      color: {
        [`${ButtonColorVariant.primary}`]: '', // default
        [`${ButtonColorVariant.neutral}`]: '',
        [`${ButtonColorVariant.error}`]: '',
      },
      size: {
        [`${ButtonSizeVariant.xs}`]:
          'h-24 px-12 has-[svg]:pl-8 has-[svg]:gap-6 [&>svg]:size-16 [&>svg]:min-w-16 py-6 typo-footer font-semibold',
        [`${ButtonSizeVariant.sm}`]:
          'h-32 px-16 has-[svg]:pl-12 has-[svg]:gap-6 [&>svg]:size-16 [&>svg]:min-w-16 py-8 typo-caption font-semibold',
        [`${ButtonSizeVariant.md}`]:
          'h-40 px-16 has-[svg]:pl-12 has-[svg]:gap-8 [&>svg]:size-20 [&>svg]:min-w-20 py-8 typo-p3 font-semibold', //default
        [`${ButtonSizeVariant.lg}`]:
          'h-48 px-20 has-[svg]:pl-16 has-[svg]:gap-8 [&>svg]:size-20 [&>svg]:min-w-20 py-12 typo-p3 font-semibold',
        [`${ButtonSizeVariant.xl}`]:
          'h-56 px-24 has-[svg]:pl-20 has-[svg]:gap-8 [&>svg]:size-24 [&>svg]:min-w-24 py-12 typo-p2 font-semibold',
      },
    },
    defaultVariants: {
      variant: ButtonVariant.filled,
      color: ButtonColorVariant.primary,
      size: ButtonSizeVariant.md,
    },
    compoundVariants: [
      {
        variant: ButtonVariant.filled,
        color: ButtonColorVariant.primary,
        class: fromHelpers.cn(
          'bg-primary-500 text-shades-white',
          'hover:bg-primary-400',
          'focus-visible:bg-primary-400',
          'active:bg-primary-500 ring-active:ring-primary-500',
          'disabled:bg-primary-200 disabled:text-shades-white',
        ),
      },
      {
        variant: ButtonVariant.filled,
        color: ButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'bg-neutral-700 text-neutral-50',
          'hover:bg-neutral-600',
          'focus-visible:bg-neutral-600',
          'active:bg-neutral-700 ring-active:ring-neutral-700',
          'disabled:bg-neutral-800 disabled:text-neutral-50/40',
        ),
      },
      {
        variant: ButtonVariant.tonal,
        color: ButtonColorVariant.primary,
        class: fromHelpers.cn(
          'bg-primary-800 text-neutral-50',
          'hover:bg-primary-700',
          'focus-visible:bg-primary-700',
          'active:bg-primary-800 ring-active:ring-primary-800',
          'disabled:bg-primary-900 disabled:text-neutral-50/40',
        ),
      },
      {
        variant: ButtonVariant.outline,
        color: ButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'text-neutral-50',
          'border border-neutral-200',
          'hover:border-neutral-300',
          'focus-visible:border-neutral-300',
          'active:bg-neutral-500/15',
          'active:border-neutral-400 ring-active:ring-neutral-400',
          'disabled:border-primary-800 disabled:text-neutral-50/40',
        ),
      },
      {
        variant: ButtonVariant.outline,
        color: ButtonColorVariant.primary,
        class: fromHelpers.cn(
          'text-neutral-50',
          'border border-primary-500',
          'hover:border-primary-400',
          'focus-visible:border-primary-400',
          'active:bg-primary-500/15',
          'active:border-primary-500 ring-active:ring-primary-500',
          'disabled:border-primary-800 disabled:text-neutral-200',
        ),
      },
      {
        variant: ButtonVariant.text,
        color: ButtonColorVariant.primary,
        class: fromHelpers.cn(
          'text-primary-500 bg-transparent',
          'hover:bg-transparency-primary-10',
          'focus-visible:bg-transparency-primary-10',
          'active:bg-transparency-primary-15 ring-active:ring-primary-500',
          'disabled:text-primary-800 disabled:bg-transparent',
        ),
      },
      {
        variant: ButtonVariant.text,
        color: ButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'text-neutral-50 bg-transparent',
          'hover:bg-transparency-white-10',
          'focus-visible:bg-transparency-white-10',
          'active:bg-transparency-white-15 ring-active:ring-neutral-500',
          'disabled:text-neutral-400 disabled:bg-transparent',
        ),
      },
    ],
  },
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: '[dmcomButton]',
  standalone: true,
  host: {
    '[class]': 'className()',
  },
  hostDirectives: [
    {
      directive: MatRipple,
      inputs: ['matRippleColor', 'matRippleDisabled', 'matRippleCentered', 'matRippleRadius', 'matRippleAnimation'],
    },
  ],
})
export class ButtonDirective {
  readonly variant = input<ButtonVariants['variant']>();
  readonly color = input<ButtonVariants['color']>();
  readonly size = input<ButtonVariants['size']>();

  protected readonly className = computed(() =>
    fromHelpers.cn(
      buttonVariants({
        variant: this.variant(),
        color: this.color(),
        size: this.size(),
      }),
    ),
  );
}
